import OwTitle from "@components/shared/OwTitle/OwTitle";
import Breadcrumbs from "@components/shared/Breadcrumbs/Breadcrumbs";
import OwButton from "@components/shared/OwButton/OwButton";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import OwCheckbox from "@components/shared/OwCheckbox/OwCheckbox";
import { OwFnInput } from "@components/shared/OwFnInput/OwFnInput";
import { useAppContextNonNull } from "@contexts/AppContext";
import IBreadcrumbs from "@models/IBreadcrumbs";
import RestEndpointsEnum from "@models/RestEndpointsEnum";
import IOnGetDeliveryMatrixDTO from "@models/DTO/IOnGetDeliveryMatrixDTO";
import { buildRestUrlWithQuery, buildAuthHeadersForFetch } from "@services/RestService";
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import ExcelJS from "exceljs";
import "./DailyRouteDeliveryMatrix.scss";
import { datadogRum } from "@datadog/browser-rum";
import OwToggle from "@components/shared/OwToggle/OwToggle";
import OwPrintButton from "@components/shared/OwPrintButton/OwPrintButton";

export const DailyRouteDeliveryMatrix = () => {
    const { supplierModel, userModel } = useAppContextNonNull();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [matrixData, setMatrixData] = useState<IOnGetDeliveryMatrixDTO | null>(null);

    const [hideZeroColumns, setHideZeroColumns] = useState(false);
    const [productFilter, setProductFilter] = useState("");

    const getBreadcrumbs = (): IBreadcrumbs[] => {
        return [
            { title: "routes", url: "/routes" },
            { title: "daily_routes", url: "/routes/daily" },
            { title: "delivery_matrix", url: "/routes/daily/delivery_matrix" },
        ];
    };

    const dailyRouteGuidsParam = searchParams.get("dailyRouteGuids") || "";

    const dailyRouteGuids = useMemo(() => {
        return dailyRouteGuidsParam ? dailyRouteGuidsParam.split(",").filter((g) => g.trim()) : [];
    }, [dailyRouteGuidsParam]);

    useEffect(() => {
        const fetchDeliveryMatrix = async () => {
            if (dailyRouteGuids.length === 0) {
                setError("No daily routes selected");
                setLoading(false);
                return;
            }

            setLoading(true);
            setError(null);

            const url = buildRestUrlWithQuery({
                endpoint: RestEndpointsEnum.DailyRouteDeliveryMatrix,
                query: { dailyRouteGuids: dailyRouteGuids.join(",") },
            });

            const headers = buildAuthHeadersForFetch({ userModel, supplierModel });

            try {
                const response = await fetch(url, { method: "GET", headers });
                const data: IOnGetDeliveryMatrixDTO = await response.json();

                if (data.success) {
                    setMatrixData(data);
                } else {
                    setError("Failed to load delivery matrix");
                }
            } catch (err) {
                console.error("Error fetching delivery matrix:", err);
                setError("Failed to load delivery matrix");
            } finally {
                setLoading(false);
            }
        };

        fetchDeliveryMatrix();
    }, [dailyRouteGuids, userModel, supplierModel]);

    // Filter catalog items based on search and zero-column toggle
    const filteredCatalogItems = useMemo(() => {
        if (!matrixData) return [];

        return matrixData.catalog_items.filter((item) => {
            // Apply product name filter
            if (productFilter.trim()) {
                const searchLower = productFilter.toLowerCase();
                const matchesName = item.catalog_item_name.toLowerCase().includes(searchLower);
                const matchesSku = item.sku.toLowerCase().includes(searchLower);
                if (!matchesName && !matchesSku) return false;
            }

            // Apply hide zero columns filter
            if (hideZeroColumns) {
                const total = matrixData.totals[item.catalog_item_id] || 0;
                if (total === 0) return false;
            }

            return true;
        });
    }, [matrixData, productFilter, hideZeroColumns]);

    // Calculate grand total of all packages
    const grandTotalPackages = useMemo(() => {
        if (!matrixData) return 0;
        return matrixData.rows.reduce((sum, row) => sum + row.total_packages, 0);
    }, [matrixData]);

    const downloadExcel = () => {
        if (!matrixData) return;

        const workbook = new ExcelJS.Workbook();
        const ws = workbook.addWorksheet("Delivery Matrix");

        // Build columns array
        const columns: { header: string; key: string; width?: number }[] = [
            { header: "Driver", key: "driver", width: 20 },
            { header: "Vehicle", key: "vehicle", width: 15 },
        ];

        // Add product columns
        filteredCatalogItems.forEach((item) => {
            columns.push({
                header: item.catalog_item_name,
                key: `item_${item.catalog_item_id}`,
                width: 12,
            });
        });

        columns.push({ header: "Total Packages", key: "total_packages", width: 15 });

        ws.columns = columns;

        // Style header row
        ws.getRow(1).font = { bold: true };
        ws.getRow(1).fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFE0E0E0" },
        };

        // Add data rows
        matrixData.rows.forEach((row) => {
            const rowData: Record<string, string | number> = {
                driver: row.driver_name || "-",
                vehicle: row.vehicle_number || "-",
                total_packages: row.total_packages,
            };

            filteredCatalogItems.forEach((item) => {
                const qty = row.catalog_item_quantities[item.catalog_item_id] || 0;
                rowData[`item_${item.catalog_item_id}`] = qty || "";
            });

            ws.addRow(rowData);
        });

        // Add totals row
        const totalsRow: Record<string, string | number> = {
            driver: "TOTAL",
            vehicle: "",
            total_packages: grandTotalPackages,
        };

        filteredCatalogItems.forEach((item) => {
            totalsRow[`item_${item.catalog_item_id}`] = matrixData.totals[item.catalog_item_id] || 0;
        });

        const totalsRowNum = ws.addRow(totalsRow);
        totalsRowNum.font = { bold: true };
        totalsRowNum.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFFFD700" },
        };

        // Generate and download
        const today = new Date().toISOString().split("T")[0];
        workbook.xlsx.writeBuffer().then((data: ArrayBuffer) => {
            const blob = new Blob([data], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const url = window.URL.createObjectURL(blob);
            const anchor = document.createElement("a");
            anchor.href = url;
            anchor.download = `delivery-matrix-${today}.xlsx`;
            anchor.click();
            window.URL.revokeObjectURL(url);
        });
    };

    const renderTable = () => {
        if (!matrixData) return null;

        return (
            <div className="matrix-table-container">
                <table className="matrix-table">
                    <thead>
                        <tr>
                            <th className="sticky-col sticky-col-1">
                                <OwLabel title="driver" />
                            </th>
                            <th className="sticky-col sticky-col-2">
                                <OwLabel title="vehicle" />
                            </th>
                            {filteredCatalogItems.map((item) => (
                                <th key={item.catalog_item_id} className="product-col">
                                    <div className="product-header">
                                        <span className="product-name">{item.catalog_item_name}</span>
                                        {item.sku && <span className="product-sku">{item.sku}</span>}
                                    </div>
                                </th>
                            ))}
                            <th className="sticky-col-right">
                                <OwLabel title="total_packages" />
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {matrixData.rows.map((row) => (
                            <tr key={row.daily_route_guid}>
                                <td className="sticky-col sticky-col-1">{row.driver_name || "-"}</td>
                                <td className="sticky-col sticky-col-2">{row.vehicle_number || "-"}</td>
                                {filteredCatalogItems.map((item) => {
                                    const qty = row.catalog_item_quantities[item.catalog_item_id] || 0;
                                    return (
                                        <td key={item.catalog_item_id} className="qty-cell">
                                            {qty || ""}
                                        </td>
                                    );
                                })}
                                <td className="sticky-col-right total-cell">{row.total_packages}</td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr className="totals-row">
                            <td className="sticky-col sticky-col-1">
                                <strong>
                                    <OwLabel title="total" />
                                </strong>
                            </td>
                            <td className="sticky-col sticky-col-2"></td>
                            {filteredCatalogItems.map((item) => {
                                const total = matrixData.totals[item.catalog_item_id] || 0;
                                return (
                                    <td key={item.catalog_item_id} className="qty-cell">
                                        <strong>{total || ""}</strong>
                                    </td>
                                );
                            })}
                            <td className="sticky-col-right total-cell">
                                <strong>{grandTotalPackages}</strong>
                            </td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="DailyRouteDeliveryMatrix">
                <Breadcrumbs crumbs={getBreadcrumbs()} />
                <OwTitle title="delivery_matrix" leftIconName="fad fa-table" />
                <div className="loading-container">
                    <i className="fas fa-spinner fa-spin me-2" />
                    <OwLabel title="loading" />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="DailyRouteDeliveryMatrix">
                <Breadcrumbs crumbs={getBreadcrumbs()} />
                <OwTitle title="delivery_matrix" leftIconName="fad fa-table" />
                <div className="error-container">
                    <i className="fas fa-exclamation-triangle me-2 text-danger" />
                    <span className="text-danger">{error}</span>
                    <OwButton title="back" className="ms-3 btn-secondary" fn={() => navigate("/routes/daily")} />
                </div>
            </div>
        );
    }

    return (
        <div className="DailyRouteDeliveryMatrix">
            <Breadcrumbs crumbs={getBreadcrumbs()} className="d-print-none" />

            <div className="row mt-2 d-print-none">
                <div className="col-12 d-flex align-items-center justify-content-between">
                    <OwTitle title="delivery_matrix" leftIconName="fad fa-table" />

                    <div>
                        <OwPrintButton />
                    </div>
                </div>
            </div>

            <div className="toolbar mb-3 d-print-none">
                <div className="d-flex flex-wrap align-items-center gap-3">
                    <div className="d-flex align-items-center">
                        <OwToggle
                            fn={(e: any) => setHideZeroColumns(e.target.value)}
                            name="hideZeroColumns"
                            label="hide_zero_columns"
                            active={hideZeroColumns}
                        />
                    </div>

                    <div className="filter-input">
                        <OwFnInput
                            name="productFilter"
                            value={productFilter}
                            fn={(e: React.ChangeEvent<HTMLInputElement>) => setProductFilter(e.target.value)}
                            placeholder="Filter products..."
                        />
                    </div>

                    <OwButton title="download_excel" leftIconClass="fas fa-file-excel" fn={downloadExcel} className="btn-success" />

                    <div className="ms-auto text-muted">
                        <small>
                            {matrixData?.rows.length || 0} routes, {filteredCatalogItems.length} products
                        </small>
                    </div>
                </div>
            </div>

            {renderTable()}
        </div>
    );
};
